import { native } from '@forest/native-services'
import { removeTrailingZeros } from '@forest/paywall-core'
import { Fit, Layout, Rive } from '@rive-app/canvas-lite'
import {
  type Component,
  createEffect,
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from 'solid-js'
import extraRewardRiv from '~/assets/rive/paywall-plus-extra-reward.riv'
import paywallRiv from '~/assets/rive/paywall.riv'
import { language, platform } from '~/lib/params'
import { m } from '~/paraglide/messages'
import {
  loadPaywallBootstrap,
  type PaywallBootstrap,
} from '~/resources/paywall-bootstrap'
import { openTrialTimelineView } from '~/store/trial-timeline-view'
import { PlusBadge } from './plus-badge'
import {
  PaywallIconBox,
  PaywallIconStamp,
  PlusIcon3x,
  PlusIconCrystal,
  PlusIconExclusive,
  PlusIconNew,
} from './plus-paywall-icons'
import { TrialTimelineView } from './trial-timeline-view'
import { Spinner } from './spinner'

type PaywallVariant = 'cna-trial' | 'cna' | 'trial' | 'subscribe'

export type PaywallEntry = 'claim-box' | 'shop-stamp'

export function PlusPaywall(props: {
  entry: PaywallEntry
  isPending?: boolean
  onSubscribe: (bootstrap: PaywallBootstrap) => void
  onClose: () => void
}) {
  const [bootstrap] = createResource(() => loadPaywallBootstrap())
  const [closeHidden, setCloseHidden] = createSignal(false)

  onMount(() => {
    const hide = () => setCloseHidden(true)
    const show = () => setCloseHidden(false)
    window.addEventListener('hide-close-button', hide)
    window.addEventListener('show-close-button', show)
    onCleanup(() => {
      window.removeEventListener('hide-close-button', hide)
      window.removeEventListener('show-close-button', show)
    })
  })

  const variant = (): PaywallVariant => {
    const b = bootstrap()
    if (platform === 'china_android') {
      return b?.chinaAndroidTrialEligibility === 'available'
        ? 'cna-trial'
        : 'cna'
    }
    if (b && (b.products.annualEarlyBird.eligible_trial_days ?? 0) > 0) {
      return 'trial'
    }
    return 'subscribe'
  }

  let pageViewLogged = false
  createEffect(() => {
    if (!bootstrap() || pageViewLogged) return
    pageViewLogged = true
    native.general.logEvent({
      event_name: 'page_view',
      event_params: {
        page_name:
          props.entry === 'claim-box'
            ? 'paywall_2026_birthday_cake_box'
            : 'paywall_2026_birthday_comeback_trees',
      },
    })
  })

  const features: Array<{
    Icon: Component<{ class?: string }>
    title: string
    sub?: string
  }> = [
    {
      Icon: PaywallIconBox,
      title: m.benefit_daily_bonus_box(),
      sub: m.during_this_event(),
    },
    {
      Icon: PaywallIconStamp,
      title: m.benefit_redeem_tree(),
      sub: m.during_this_event(),
    },
    {
      Icon: PlusIconExclusive,
      title: m.plus_exclusive_tree(),
    },
    {
      Icon: PlusIconNew,
      title: m.subscription_seasonal_new_content(),
    },
    {
      Icon: PlusIconCrystal,
      title: m.subscription_selected_content(),
    },
    {
      Icon: PlusIcon3x,
      title: m.onboarding_paywall_benefit_triple_coin(),
    },
  ]

  const ctaLabel = () => {
    const v = variant()
    if (v === 'cna-trial') {
      return m.onboarding_paywall_start_triasl_btn_cna({
        trial_day_number: 7,
      })
    }
    if (v === 'cna') return m.onboarding_paywall_start_subscribe_btn_cna()
    if (v === 'trial') return m.btn_zero_fee_trial()
    if (!bootstrap()) return ''
    return m.btn_unlock_plus()
  }

  const footerText = () => {
    const b = bootstrap()
    const v = variant()
    if (v === 'cna-trial') return m.onboarding_paywall_footer_trial_cna()
    if (v === 'cna') return m.onboarding_paywall_footer_cna()
    if (!b) return ''
    const earlyBird = b.products.annualEarlyBird
    const planPrice = removeTrailingZeros(earlyBird.price_localized)
    if (v === 'trial') {
      return m.onboarding_paywall_footer({
        trial_day_number: String(earlyBird.eligible_trial_days),
        plan_price: planPrice,
      })
    }
    return m.subscription_footer_yearly({ yearly_price: planPrice })
  }

  let kvCanvas: HTMLCanvasElement | undefined
  onMount(() => {
    if (!kvCanvas) return
    const rive = new Rive({
      canvas: kvCanvas,
      src: props.entry === 'claim-box' ? extraRewardRiv : paywallRiv,
      artboard: 'KV',
      stateMachines: ['State Machine 1'],
      autoplay: true,
      layout: new Layout({ fit: Fit.FitHeight }),
      onLoad: () => rive.resizeDrawingSurfaceToCanvas(),
    })
    const onResize = () => rive.resizeDrawingSurfaceToCanvas()
    window.addEventListener('resize', onResize)
    onCleanup(() => {
      window.removeEventListener('resize', onResize)
      rive.cleanup()
    })
  })

  const showSpinner = () => props.isPending || bootstrap.loading

  const fireSubscribe = () => {
    const b = bootstrap()
    if (!b) return
    props.onSubscribe(b)
  }

  const handleCtaClick = () => {
    if (variant() === 'trial') {
      openTrialTimelineView()
      return
    }
    fireSubscribe()
  }

  const openPlanDetails = () => {
    const base =
      import.meta.env.MODE === 'staging'
        ? `https://forest-permanent-paywall-staging.seekrtech.com/${language}/details/`
        : `https://forest-permanent-paywall.seekrtech.com/${language}/details/`
    location.href = `${base}?platform=${platform}`
  }

  return (
    <div class="fixed inset-0 z-[80] flex flex-col overflow-y-auto text-white">
      <div class="fixed left-0 right-0 top-0 z-10 flex justify-end px-4 pt-[calc(var(--safe-area-top)+16px)]">
        <Show when={!closeHidden()}>
          <button
            type="button"
            onClick={props.onClose}
            class="grid size-9 place-items-center rounded-full bg-white/80 ring-1 ring-inset ring-white/15 backdrop-blur"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.28613 4.28516L12.8575 12.8565"
                stroke="#333333"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M4.28605 12.8565L12.8574 4.28516"
                stroke="#333333"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </Show>
      </div>

      <div class="relative">
        <div
          class="absolute inset-0"
          style={{
            background:
              'radial-gradient(105% 42% at 50% 32%, rgba(255, 220, 180, 0.6) 0%, rgba(255, 130, 160, 0.25) 45%, rgba(255, 85, 116, 0) 88%), radial-gradient(171.69% 90.62% at 166.67% 109.38%, #7B72F2 0%, #611B53 100%)',
          }}
        />

        <div class="relative mx-auto flex w-full max-w-[640px] flex-1 flex-col items-center gap-1.5 px-9 pb-48 pt-[calc(var(--safe-area-top)+32px)]">
          <header class="flex w-full max-w-[329px] flex-col items-center gap-2">
            <div class="flex items-center gap-2 text-base font-semibold leading-[22px]">
              <span>Forest</span>
              <PlusBadge class="h-[22px]" />
            </div>
            <h1
              class="whitespace-pre-line text-center text-[32px] font-bold leading-[44px] text-white"
              style={{
                'text-shadow': '0 0 17.8px rgba(249, 213, 164, 0.32)',
              }}
            >
              {props.entry === 'claim-box'
                ? m.birthday_event_paywall_title_box()
                : m.birthday_event_paywall_title()}
            </h1>
          </header>

          <div class="my-1 h-[35vh] w-screen">
            <canvas
              ref={(el) => (kvCanvas = el)}
              class="size-full"
              aria-hidden="true"
            />
          </div>

          <ul class="flex w-full flex-col gap-5 rounded-[20px] border-[3px] border-[rgba(253,240,175,0.1)] bg-[rgba(253,240,175,0.1)] p-6">
            <For each={features}>
              {(feature) => (
                <li class="flex items-center gap-3">
                  <feature.Icon class="size-[42px] shrink-0" />
                  <div class="flex min-w-0 flex-1 flex-col gap-1">
                    <p class="text-sm font-semibold leading-5">
                      {feature.title}
                    </p>
                    <p
                      class="text-xs leading-4 text-white/50"
                      classList={{ hidden: !feature.sub }}
                    >
                      {feature.sub}
                    </p>
                  </div>
                </li>
              )}
            </For>

            {/* button plan details */}
            <li>
              <button
                type="button"
                onClick={openPlanDetails}
                class="flex w-full items-center justify-center gap-1 text-sm font-bold text-white/60"
              >
                <span>{m.daily_paywall_learn_more_plus()}</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.45499 9.96004L7.71499 6.70004C8.09999 6.31504 8.09999 5.68504 7.71499 5.30004L4.45499 2.04004"
                    stroke="currentColor"
                    stroke-width="1.02857"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div class="fixed bottom-0 left-0 right-0 flex w-full flex-col items-center gap-2 pb-12 pt-3">
        {/* Progressive backdrop blur — mask makes the 18.2px blur fade in
            from 0 at the top to full at ~50% so the top edge dissolves into
            the paywall bg instead of cutting on a hard horizontal line. */}
        <div
          aria-hidden
          class="pointer-events-none absolute inset-0 backdrop-blur-[10px]"
          style={{
            'mask-image':
              'linear-gradient(to bottom, transparent 0%, black 50%, black 100%)',
            '-webkit-mask-image':
              'linear-gradient(to bottom, transparent 0%, black 50%, black 100%)',
          }}
        />
        {/* Green wash on top of the blurred backdrop, same gradient as before. */}
        <div
          aria-hidden
          class="pointer-events-none absolute inset-0 bg-gradient-to-b from-[rgba(210,210,210,0)] from-[16.319%] via-[rgba(20,122,100,0.2)] via-[64.597%] to-[rgba(13,88,86,0.2)]"
        />
        <div class="relative mx-auto flex w-full max-w-[640px] flex-col items-center gap-2 px-6">
          <button
            type="button"
            disabled={showSpinner()}
            onClick={handleCtaClick}
            class="group h-[52px] w-full rounded-[12px] p-px transition-transform [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)] active:scale-[0.98] disabled:opacity-60"
          >
            <span class="flex h-full w-full items-center justify-center rounded-[11px] text-base font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              <Show
                when={!showSpinner()}
                fallback={
                  <Spinner variant="circular" class="size-6 text-[#176361]" />
                }
              >
                <span>{ctaLabel()}</span>
              </Show>
            </span>
          </button>
          <p class="text-center text-xs leading-4 text-white">{footerText()}</p>
        </div>
      </div>

      <TrialTimelineView
        isPending={props.isPending}
        footerText={footerText()}
        onStartTrial={fireSubscribe}
      />
    </div>
  )
}
